import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getActressBySlug, getHotActresses } from '@/mocks/actresses';
import { actressImages } from '@/mocks/actressImages';
import ActressCard from '@/components/base/ActressCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const actress = getActressBySlug(slug || '');
  const relatedActresses = getHotActresses().filter((a) => a.slug !== slug).slice(0, 4);

  if (!actress) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-user-unfollow-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600">女优资料未找到</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 mt-3 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const imageUrl = actressImages[actress.slug] || '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${actress.name}（${actress.nameJp}）- 日本女优详细介绍`,
    description: actress.description,
    url: `https://www.ribennvyou.com/article/${actress.slug}`,
    inLanguage: 'zh-CN',
    author: {
      '@type': 'Organization',
      name: '日本女优大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '日本女优大全',
    },
    datePublished: `${actress.debutYear}-01-01`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.ribennvyou.com/article/${actress.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${actress.name}（${actress.nameJp}）- 日本女优详细介绍 | 日本女优大全`}
        description={`${actress.name}（${actress.nameJp}），${actress.debutYear}年出道，${actress.description}代表作品包括${actress.representativeWorks.slice(0, 3).join('、')}等。`}
        keywords={`${actress.name},${actress.nameJp},日本女优,日本女优资料,${actress.category},${actress.tags.join(',')}`}
        canonicalUrl={`https://www.ribennvyou.com/article/${actress.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-14 bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: actress.category, to: `/category/${actress.category === '影视剧女优' ? 'drama-actress' : actress.category === '偶像女优' ? 'idol-actress' : actress.category === '新生代女优' ? 'new-generation' : actress.category === '实力派女优' ? 'powerhouse-actress' : actress.category === '晨间剧女优' ? 'morning-drama' : 'popular-ranking'}` },
                { label: actress.name },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="sticky top-24">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-4">
                  <img
                    src={imageUrl}
                    alt={`${actress.name} ${actress.nameJp} 日本女优`}
                    className="w-full h-full object-cover object-top"
                    title={`${actress.name} - ${actress.category}`}
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {actress.hot && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-500 text-background-50 font-medium whitespace-nowrap">
                    热门
                  </span>
                )}
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium whitespace-nowrap">
                  {actress.category}
                </span>
                {actress.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {actress.name}
              </h1>
              <p className="text-base text-foreground-500 mb-6">{actress.nameJp}</p>

              <p className="text-sm text-foreground-700 leading-relaxed mb-8">
                {actress.description}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-5 bg-background-100 rounded-xl">
                {[
                  { label: '出道年份', value: `${actress.debutYear}年` },
                  { label: '出生日期', value: actress.birthDate },
                  { label: '出生地', value: actress.birthplace },
                  { label: '身高', value: actress.height },
                  { label: '血型', value: actress.bloodType },
                  { label: '所属事务所', value: actress.agency },
                ].map((info) => (
                  <div key={info.label}>
                    <span className="text-xs text-foreground-500 block mb-0.5">{info.label}</span>
                    <span className="text-sm font-medium text-foreground-900">{info.value}</span>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  个人简介
                </h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
                  {actress.biography.split('\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                  代表作品
                </h2>
                <div className="flex flex-wrap gap-2">
                  {actress.representativeWorks.map((work) => (
                    <span
                      key={work}
                      className="px-3 py-1.5 bg-background-100 rounded-lg text-sm text-foreground-700 border border-background-200/70 whitespace-nowrap"
                    >
                      {work}
                    </span>
                  ))}
                </div>
              </div>

              {actress.awards.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                    获奖记录
                  </h2>
                  <ul className="space-y-2">
                    {actress.awards.map((award, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-medal-line text-accent-600 text-xs"></i>
                        </div>
                        {award}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {relatedActresses.length > 0 && (
            <section className="border-t border-background-200/70 pt-12">
              <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {relatedActresses.map((rel) => (
                  <ActressCard key={rel.id} actress={rel} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}